import type {
  RedemptionFundResult,
  RedemptionSummary,
} from './calculator.types';

export const REDEEM_MODES = {
  SINGLE: 'SINGLE',
  MULTIPLE: 'MULTIPLE',
  ENTIRE: 'ENTIRE',
} as const;

export const AMOUNT_TYPES = {
  ALL_SHARES: 'ALL_SHARES',
  SHARES: 'SHARES',
  DOLLAR_GROSS: 'DOLLAR_GROSS',
  DOLLAR_NET: 'DOLLAR_NET',
  ANNUAL_FREE_ALLOCATION: 'ANNUAL_FREE_ALLOCATION',
  DSC_FREE: 'DSC_FREE',
} as const;

export const DEFAULT_RESULT_CURRENCY = 'CAD';

export const EMPTY_RESULT_PLACEHOLDER = '-';

export type ResultFieldConfig<T> = {
  dividerBefore?: boolean;
  emphasis?: boolean;
  fieldKey: string;
  hideWhenWithholdingTaxHidden?: boolean;
  labelKey: string;
  getValue: (source: T) => string | number | null | undefined;
};

export const POSITION_RESULT_FIELDS: ResultFieldConfig<RedemptionFundResult>[] =
  [
    {
      fieldKey: 'priceDate',
      labelKey: 'calculator.results.priceDate',
      getValue: (fundResult) => fundResult.priceDate,
    },
    {
      fieldKey: 'currency',
      labelKey: 'calculator.results.currency',
      getValue: (fundResult) => fundResult.currency,
    },
    {
      fieldKey: 'unitPrice',
      labelKey: 'calculator.results.unitPrice',
      getValue: (fundResult) => fundResult.unitPrice,
    },
    {
      fieldKey: 'tenPercentAmount',
      labelKey: 'calculator.results.tenPercentAmount',
      getValue: (fundResult) => fundResult.tenPercentFreeDollarAmount,
    },
    {
      fieldKey: 'backEndFeePercentage',
      labelKey: 'calculator.results.backEndFeePercentage',
      getValue: (fundResult) => fundResult.backEndFeePercent,
    },
    {
      dividerBefore: true,
      emphasis: true,
      fieldKey: 'marketValue',
      labelKey: 'calculator.results.marketValue',
      getValue: (fundResult) => fundResult.marketValue,
    },
  ];

export const FUND_REDEMPTION_RESULT_FIELDS: ResultFieldConfig<RedemptionFundResult>[] =
  [
    {
      fieldKey: 'grossRedemption',
      labelKey: 'calculator.results.grossRedemption',
      getValue: (fundResult) => fundResult.grossRedemption,
    },
    {
      fieldKey: 'backEndRedemptionFee',
      labelKey: 'calculator.results.backEndRedemptionFee',
      getValue: (fundResult) => fundResult.backEndRedemptionFee,
    },
    {
      dividerBefore: true,
      fieldKey: 'grossProceeds',
      labelKey: 'calculator.results.grossProceeds',
      getValue: (fundResult) => fundResult.grossProceeds,
    },
    {
      fieldKey: 'withholdingTax',
      hideWhenWithholdingTaxHidden: true,
      labelKey: 'calculator.results.withholdingTax',
      getValue: (fundResult) => fundResult.withholdingTax,
    },
    {
      dividerBefore: true,
      emphasis: true,
      fieldKey: 'netAmount',
      labelKey: 'calculator.results.netAmount',
      getValue: (fundResult) => fundResult.netAmount,
    },
  ];

export const SUMMARY_RESULT_FIELDS: ResultFieldConfig<RedemptionSummary>[] = [
  {
    fieldKey: 'grossRedemption',
    labelKey: 'calculator.results.grossRedemption',
    getValue: (summary) => summary.grossRedemption,
  },
  {
    fieldKey: 'backEndRedemptionFee',
    labelKey: 'calculator.results.backEndRedemptionFee',
    getValue: (summary) => summary.backEndRedemptionFee,
  },
  {
    dividerBefore: true,
    fieldKey: 'grossProceeds',
    labelKey: 'calculator.results.grossProceeds',
    getValue: (summary) => summary.grossProceeds,
  },
  {
    fieldKey: 'withholdingTax',
    hideWhenWithholdingTaxHidden: true,
    labelKey: 'calculator.results.withholdingTax',
    getValue: (summary) => summary.withholdingTax,
  },
  {
    dividerBefore: true,
    emphasis: true,
    fieldKey: 'netAmount',
    labelKey: 'calculator.results.netAmount',
    getValue: (summary) => summary.netAmount,
  },
];
